import { useEffect, useState } from "react";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Ch } from "@/components/Fa";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";

const nf = (x: number) => (Math.abs(x ?? 0) >= 100000 ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x) : Intl.NumberFormat("id-ID").format(x ?? 0));
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };

export function ClustersPage({ wing }: { wing: "social" | "media" }) {
  const today = new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);
  const [run, setRun] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [openCluster, setOpenCluster] = useState<string | null>(null);
  const [sort, setSort] = useState<"rank" | "neg" | "pos">("rank");
  const [members, setMembers] = useState<Record<string, { rows: any[]; total: number; page: number; loading: boolean }>>({});
  async function loadMembers(cid: string, page = 1) {
    setMembers((m) => ({ ...m, [cid]: { ...(m[cid] ?? { rows: [], total: 0 }), page, loading: true } }));
    const j = await fetch(`/api/v1/clusters/${cid}/mentions?page=${page}&pageSize=50`).then((r) => r.json()).catch(() => null);
    setMembers((m) => ({ ...m, [cid]: {
      rows: page === 1 ? (j?.data ?? []) : [...(m[cid]?.rows ?? []), ...(j?.data ?? [])],
      total: j?.meta?.total ?? 0, page, loading: false,
    } }));
  }
  function toggleCluster(cid: string) {
    if (openCluster === cid) { setOpenCluster(null); return; }
    setOpenCluster(cid);
    if (!members[cid]) loadMembers(cid, 1);
  }

  async function load(S = start, E = end) {
    const r = await fetch(`/api/v1/clusters?wing=${wing}&start=${S}&end=${E}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json();
    setRun(j.data);
    return j.data;
  }
  async function compute(S = start, E = end) {
    setBusy(true); setMsg("Menghitung cluster (deterministik, dari data ter-ingest)…");
    const r = await fetch(`/api/v1/clusters/run?wing=${wing}&start=${S}&end=${E}`, { method: "POST" });
    const j = await r.json();
    setBusy(false);
    if (!r.ok) { setMsg(j.error ?? "gagal"); return; }
    setMsg("");
    load(S, E);
  }
  useEffect(() => { load().then((d) => { if (!d) compute(); }); }, []);

  async function jadikanIssue(c: any) {
    const t = prompt("Judul issue:", c.label);
    if (!t) return;
    const ids = (c.docs ?? []).map((d: any) => d.mention.id);
    const r = await fetch("/api/v1/issues", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title: t, description: c.summary, mentionIds: ids }) });
    const j = await r.json();
    if (r.ok) location.href = `/a/issues/${j.data.id}`; else alert(j.error);
  }

  const clustersRaw = (run?.clusters ?? []) as any[];
  const volTotal = clustersRaw.reduce((a, c) => a + c.volume, 0);
  const negTotal = clustersRaw.reduce((a, c) => a + c.negative, 0);
  const naik = clustersRaw.filter((c) => (c.trendPct ?? 0) > 0).length;
  // Urutan tampil: relevansi (rank, default) / paling negatif / paling positif — berdasarkan share sentimen.
  const clusters = [...clustersRaw].sort((a, b) => {
    if (sort === "rank") return (a.rank ?? 0) - (b.rank ?? 0);
    const share = (x: any) => ((sort === "neg" ? x.negative : x.positive) / ((x.positive + x.neutral + x.negative) || 1));
    const d = share(b) - share(a);
    return d !== 0 ? d : (a.rank ?? 0) - (b.rank ?? 0); // tiebreak: relevansi
  });

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Issue Clustering</h1>
          <div className="avio-t-sm avio-muted">
            {wing === "social" ? "Social · komunitas label AI vendor (co-occurrence)" : "Media massa · kemiripan kosakata judul + lead (TF-IDF)"} · deterministik, tanpa LLM
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "var(--space-5)", alignItems: "center", flexWrap: "wrap" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2).then((d) => { if (!d) compute(s2, e2); }); }} />
          <select className="avio-select" data-size="sm" value={sort} onChange={(e) => setSort(e.target.value as "rank" | "neg" | "pos")} title="Urutkan klaster">
            <option value="rank">Urut: Relevansi</option>
            <option value="neg">Urut: Paling Negatif</option>
            <option value="pos">Urut: Paling Positif</option>
          </select>
          <button className="avio-btn" data-variant="primary" onClick={() => compute()} disabled={busy}>
            <i className={busy ? "fa-solid fa-rotate fa-spin" : "fa-solid fa-rotate"} /> {busy ? "Menghitung…" : "Hitung ulang"}
          </button>
        </div>
      </div>
      <SyncBar wing={wing === "social" ? "social" : "media"} />

      {msg && <div className="avio-banner" data-tone="info"><i className="fa-solid fa-circle-info avio-banner__icon" /><div>{msg}</div></div>}

      {clusters.length > 0 && (
        <div className="avio-tiles">
          <Tile c={1} icon="fa-solid fa-diagram-project" label="Klaster terbentuk" value={clusters.length} sub="isu berbeda" />
          <Tile c={3} icon="fa-solid fa-layer-group" label="Item terklaster" value={volTotal} sub={wing === "social" ? "mention" : "artikel"} />
          <Tile c={2} icon="fa-solid fa-circle-exclamation" label="Item negatif" value={negTotal} sub="di seluruh klaster" />
          <Tile c={4} icon="fa-solid fa-arrow-trend-up" label="Klaster menguat" value={naik} sub="vs periode sebelumnya" />
        </div>
      )}

      {run && <div className="avio-t-sm avio-muted">Snapshot: {new Date(run.createdAt).toLocaleString("id-ID")}</div>}

      <div className="avio-grid">
        {clusters.map((c: any) => {
          const tot = c.positive + c.neutral + c.negative || 1;
          const up = (c.trendPct ?? 0) >= 0;
          const opened = openCluster === c.id;
          return (
            <div key={c.id} data-col="12">
              <Panel
                icon="fa-solid fa-diagram-project"
                title={<span style={{ cursor: "pointer" }} onClick={() => toggleCluster(c.id)} title="Klik untuk melihat semua item dalam klaster ini">
                  <span className="avio-item__rank" style={{ marginRight: 6 }}>#{c.rank}</span>{c.label}
                </span>}
                action={
                  <span style={{ display: "flex", alignItems: "center", gap: "var(--space-5)" }}>
                    <span className="avio-trend" data-dir={up ? "up" : "down"} data-good="false">
                      <i className={`fa-solid fa-arrow-trend-${up ? "up" : "down"}`} />
                      {c.trendPct > 0 ? "+" : ""}{c.trendPct}%
                    </span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>{nf(c.volume)}</b>
                    <button className="avio-btn" data-variant="secondary" data-size="sm" onClick={() => jadikanIssue(c)}>
                      <i className="fa-solid fa-fire-extinguisher" /> Jadikan issue
                    </button>
                  </span>
                }>
                <p className="avio-t-sm" style={{ margin: "0 0 var(--space-6)", color: "var(--color-text-secondary)" }}>{c.summary}</p>

                <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 460, marginBottom: "var(--space-6)" }}>
                  <span className="avio-prow__track">
                    {c.positive > 0 && <span className="avio-prow__fill" title={`positif ${c.positive}`} style={{ flex: c.positive, background: "var(--mark-pos)" }} />}
                    {c.neutral > 0 && <span className="avio-prow__fill" title={`netral ${c.neutral}`} style={{ flex: c.neutral, background: "var(--mark-neu)" }} />}
                    {c.negative > 0 && <span className="avio-prow__fill" title={`negatif ${c.negative}`} style={{ flex: c.negative, background: "var(--mark-neg)" }} />}
                  </span>
                  <span className="avio-sub" style={{ fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: "var(--sent-pos)" }}>{c.positive} positif</span> · {c.neutral} netral ·{" "}
                    <span style={{ color: "var(--sent-neg)" }}>{c.negative} negatif</span> · {((c.negative / tot) * 100).toFixed(0)}% negatif
                  </span>
                </div>

                <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", marginBottom: "var(--space-6)" }}>
                  {(c.phrases ?? []).map((p2: any, i: number) => (
                    <a key={i} className="avio-chip" data-tone="soft"
                      href={wing === "social" && p2.ref ? `/a/social/posts?label=${p2.ref}&start=${start}&end=${end}` : `/a/monitoring/articles?q=${encodeURIComponent(p2.name)}&start=${start}&end=${end}`}>
                      <span className="avio-chip__label">{p2.name}</span>
                    </a>
                  ))}
                </div>

                {c.repSentence && (
                  <blockquote style={{ margin: "0 0 var(--space-6)", padding: "var(--space-4) var(--space-6)", borderLeft: "3px solid var(--color-border-strong)", color: "var(--color-text-secondary)", fontStyle: "italic", fontSize: "var(--text-base)" }}>
                    &ldquo;{c.repSentence}&rdquo;
                  </blockquote>
                )}

                <div className="avio-overline" style={{ marginBottom: "var(--space-3)" }}>Evidence teratas</div>
                <div className="avio-list">
                  {(c.docs ?? []).map((d: any) => (
                    <a key={d.mentionId} className="avio-item" href="#" style={{ paddingLeft: 0, paddingRight: 0 }}
                      onClick={(e) => { e.preventDefault(); setDetailId(d.mention.id); }}>
                      <span className="avio-item__main">
                        <span className="avio-item__title">{((d.mention.title || d.mention.content || "").replace(/\s+/g, " ")).slice(0, 120)}</span>
                        <span className="avio-item__meta"><Ch t={d.mention.sourceType} /> {d.mention.sourceType}</span>
                      </span>
                      {d.mention.sentimentLabel && (
                        <span className="avio-chip" data-sentiment={d.mention.sentimentLabel}>
                          <span className="avio-chip__label">{TONE_ID[d.mention.sentimentLabel] ?? d.mention.sentimentLabel}</span>
                        </span>
                      )}
                    </a>
                  ))}
                </div>

                <button className="avio-btn" data-variant="ghost" data-size="sm" onClick={() => toggleCluster(c.id)} style={{ marginTop: "var(--space-5)" }}>
                  <i className={opened ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"} />
                  {opened ? " Tutup daftar lengkap" : ` Lihat semua ${nf(c.volume)} item`}
                </button>

                {opened && (
                  <div style={{ marginTop: "var(--space-5)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-md)", padding: "var(--space-5) var(--space-6)" }}>
                    <div className="avio-overline" style={{ marginBottom: "var(--space-3)" }}>
                      {members[c.id]?.total ?? "…"} item dalam klaster ini
                    </div>
                    <div className="avio-list">
                      {(members[c.id]?.rows ?? []).map((d: any) => (
                        <a key={d.id} className="avio-item" href="#" style={{ paddingLeft: 0, paddingRight: 0 }}
                          onClick={(e) => { e.preventDefault(); setDetailId(d.id); }}>
                          <span className="avio-item__main">
                            <span className="avio-item__title">{String(d.title).slice(0, 130)}</span>
                            <span className="avio-item__meta">
                              {new Date(new Date(d.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(5, 16).replace("T", " ")} ·
                              <Ch t={d.sourceType} /> {d.media ?? (d.author ? "@" + d.author : d.sourceType)}
                            </span>
                          </span>
                          {d.tone && <span className="avio-chip" data-sentiment={d.tone}><span className="avio-chip__label">{TONE_ID[d.tone] ?? d.tone}</span></span>}
                        </a>
                      ))}
                    </div>
                    {members[c.id]?.loading && <div className="avio-sub" style={{ padding: "var(--space-5) 0" }}><i className="fa-solid fa-rotate fa-spin" /> memuat…</div>}
                    {!members[c.id]?.loading && (members[c.id]?.rows?.length ?? 0) < (members[c.id]?.total ?? 0) && (
                      <button className="avio-btn" data-variant="secondary" data-size="sm" style={{ marginTop: "var(--space-5)" }}
                        onClick={() => loadMembers(c.id, (members[c.id]?.page ?? 1) + 1)}>
                        Muat 50 lagi ({members[c.id]?.rows.length}/{members[c.id]?.total})
                      </button>
                    )}
                  </div>
                )}
              </Panel>
            </div>
          );
        })}
      </div>

      {run && clusters.length === 0 && (
        <Panel icon="fa-solid fa-diagram-project" title="Belum ada klaster">
          <div className="avio-empty">
            <i className="fa-regular fa-face-meh avio-empty__icon" />
            <div className="avio-empty__title">Belum ada klaster bermakna pada rentang ini.</div>
            <div className="avio-empty__hint">Butuh volume dan label yang cukup — coba rentang tanggal yang lebih panjang.</div>
          </div>
        </Panel>
      )}
      {!run && !busy && (
        <Panel icon="fa-solid fa-diagram-project" title="Belum ada snapshot">
          <div className="avio-empty">
            <i className="fa-regular fa-clock avio-empty__icon" />
            <div className="avio-empty__title">Belum ada snapshot untuk rentang ini.</div>
            <div className="avio-empty__hint">Klik “Hitung ulang” untuk membentuk klaster.</div>
          </div>
        </Panel>
      )}
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
